import { Button } from '@/components/ui/button'
import { Form } from '@/components/ui/form'
import Card from './Card'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  FormStateContext,
  FormEntityContext,
} from 'app/frontend/base/config/context/forms'
import { useUpdateEntity } from 'app/frontend/base/config/mutations'
import { PropsWithChildren, useContext, useEffect, useState } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { z, ZodTypeAny } from 'zod'
import { isEqual } from 'lodash'
import { deleteRequest } from '../api'
import { displayErrorToast } from '../helpers'

interface Identifiable {
  id?: number
}

const isEmptyish = (value: unknown) =>
  value === undefined ||
  value === null ||
  value === false ||
  value === '' ||
  (Array.isArray(value) && value.length === 0) ||
  (typeof value === 'object' && value !== null && !Object.keys(value).length)

const normalize = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(normalize).filter((item) => !isEmptyish(item))
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce<Record<string, unknown>>(
      (result, [key, item]) => {
        const normalized = normalize(item)
        if (!isEmptyish(normalized)) result[key] = normalized
        return result
      },
      {},
    )
  }

  return value
}

const differsFromDefaults = (values: unknown, defaults: unknown) =>
  !isEqual(normalize(values), normalize(defaults))

export interface SubmitButtonsProps {
  isPending: boolean
  isDeleting: boolean
  setValue: (name: string, value: unknown) => void
}

interface Props<T> {
  formSchema: ZodTypeAny
  entity: T
  entityType: string
  parentEntityType?: string
  parentId?: number
  onSave?: (form: UseFormReturn, data: RecursivePartial<T>) => void
  extraActions?: React.ReactNode[]
  allowDelete?: boolean
  i18nPrefix?: string
  fieldEntityType?: string
  stringifyValues?: (keyof T)[]
  transformValues?: (values: z.infer<ZodTypeAny>) => z.infer<ZodTypeAny>
  className?: string
  submitButtons?: (props: SubmitButtonsProps) => React.ReactNode
  showSuccessToast?: boolean
  customSubmit?: (
    form: UseFormReturn,
    values: z.infer<ZodTypeAny>,
  ) => Promise<void> | void
}

const FormWrapper = <T extends Identifiable>({
  formSchema,
  entity,
  entityType,
  parentEntityType,
  parentId,
  onSave,
  extraActions,
  allowDelete,
  transformValues,
  children,
  className,
  stringifyValues,
  i18nPrefix = 'forms.fields',
  fieldEntityType,
  submitButtons,
  showSuccessToast = true,
  customSubmit,
}: PropsWithChildren<Props<T>>) => {
  const { t } = useTranslation()
  const reduceMotion = useReducedMotion()
  const { setHasUnsavedChanges } = useContext(FormStateContext)
  const [deleting, setDeleting] = useState(false)
  const mutation = useUpdateEntity<T>({
    entityType,
    id: entity.id,
    queryKey: entityType === 'team' || entityType === 'guild' ? 'path' : 'id',
    queryKeyValue:
      entityType === 'team'
        ? (entity as unknown as wowaudit.Team).path
        : entityType === 'guild'
        ? (entity as unknown as wowaudit.Guild).path
        : entity.id,
    parentEntityType,
    parentId,
    stringifyValues,
    showSuccessToast,
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: entity,
  })
  const currentValues = form.watch()
  const isReallyDirty =
    form.formState.isDirty &&
    differsFromDefaults(currentValues, form.formState.defaultValues)
  const showUnsavedOverlay =
    isReallyDirty && !!setHasUnsavedChanges && !submitButtons

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    const transformedValues = transformValues ? transformValues(values) : values

    if (customSubmit) {
      await customSubmit(form, transformedValues)
      return
    }

    mutation.mutate(transformedValues, {
      onSuccess: (data) => {
        if (onSave) {
          onSave(form, data)
        } else {
          form.reset(data)
        }
      },
    })
  }
  const isSubmitting = customSubmit
    ? form.formState.isSubmitting
    : mutation.isPending

  const saveButton = (
    <Button type='submit' disabled={deleting} loading={isSubmitting} size='sm'>
      {entity.id ? t('shared.save-changes') : t('shared.save-new')}
    </Button>
  )

  const resetButton = (
    <Button
      type='button'
      variant='ghost'
      size='sm'
      disabled={deleting || isSubmitting}
      onClick={() => form.reset()}
    >
      {t('shared.reset')}
    </Button>
  )

  useEffect(() => {
    if (setHasUnsavedChanges) setHasUnsavedChanges(isReallyDirty)

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (isReallyDirty) {
        event.preventDefault()
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isReallyDirty, setHasUnsavedChanges])

  if (Object.keys(form.formState.errors).length) {
    console.log({ formErrors: form.formState.errors })
  }

  return (
    <FormEntityContext.Provider
      value={{ entityType: fieldEntityType ?? entityType, i18nPrefix }}
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {children}

          <div
            className={`flex justify-end mt-6 gap-4 items-center ${
              className ?? ''
            }`}
          >
            {Object.keys(form.formState.errors).length ? (
              <span className='text-destructive-foreground font-semibold'>
                {t('shared.errors-found')}
              </span>
            ) : null}

            {extraActions ? extraActions : null}

            {allowDelete && entity.id ? (
              <Button
                variant='destructive'
                size='sm'
                type='button'
                loading={deleting}
                disabled={isSubmitting}
                onClick={() => {
                  if (confirm(t('shared.delete-confirm'))) {
                    const baseUrl = parentEntityType
                      ? `/api/${parentEntityType}s/${parentId}/${entityType}s`
                      : `/api/${entityType}s`

                    deleteRequest(`${baseUrl}/${entity.id}`)
                      .then(() => {
                        setDeleting(false)
                        if (onSave) onSave(form, entity as RecursivePartial<T>)
                      })
                      .catch((err) => {
                        displayErrorToast(err, t)
                        setDeleting(false)
                      })
                  }
                }}
              >
                {t('shared.delete')}
              </Button>
            ) : null}
            {submitButtons
              ? submitButtons({
                  isPending: isSubmitting,
                  isDeleting: deleting,
                  setValue: form.setValue,
                })
              : saveButton}
          </div>

          <AnimatePresence>
            {showUnsavedOverlay ? (
              <motion.div
                className='fixed inset-x-0 bottom-0 z-50 flex justify-center p-4 pointer-events-none'
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 80 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 80 }}
                transition={
                  reduceMotion
                    ? { duration: 0.15 }
                    : { type: 'spring', stiffness: 460, damping: 22 }
                }
              >
                <Card
                  size='sm'
                  className='pointer-events-auto flex flex-row items-center gap-3 shadow-lg'
                >
                  <span className='text-sm text-muted-foreground whitespace-nowrap'>
                    {t('forms.unsaved-changes')}
                  </span>
                  {resetButton}
                  {saveButton}
                </Card>
              </motion.div>
            ) : null}
          </AnimatePresence>
        </form>
      </Form>
    </FormEntityContext.Provider>
  )
}

export default FormWrapper
